import React, { FC, useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import { Button } from "@natera/form";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import {
  ConfigContext,
  ProfileContext,
  TestHistoryContext,
  UserContext,
} from "@app/provider";
import { SpinnerView } from "@app/components";
import {
  formatPastedPhoneNumber,
  formatPhoneNumberInput,
  stripPhoneFormatting,
  validateEmail,
} from "@app/utils";
import { BlueHeaderContent } from "@app/components/ui/layout";
import InfoIcon from "@app/neva/assets/svg/info-icon.svg";
import ChevronUp from "@app/postBill/assets/svg/chevron-up.svg";
import ChevronDown from "@app/postBill/assets/svg/chevron-down.svg";
import IntakeFormService, {
  classifyCounselingIntent,
  GetActiveOncUppGisTriageResponse,
  GisIntakeStep,
  MarkOncUppGisTriageIntakeStartedResponse,
  STEP_ROUTE_SUFFIX,
  UpdateOncUppGisTriageStepResponse,
} from "@app/service/intakeForm";
import { TestDetailsRecord } from "@app/provider/types";
import TestCardService from "@app/service/testCard";
import "./intakeFormConfirmInfo.scss";
import { routes } from "@app/routing";
import {
  GIS_ENTRY_SOURCE_PUBLIC,
  GIS_NO_ORDER_UID,
  clearGisReturnUrl,
  getGisEntrySource,
} from "@app/utils/gisEntrySource";
import { useEligibleGisOrders } from "@app/pages/private/gisTriage/useEligibleGisOrders";
import { useGisIntakeFormData } from "@app/hooks/useGisIntakeFormData";
import { useIntakeForm } from "./context";
import { useBookingConfirmedRedirect } from "./useBookingConfirmedRedirect";

const messages = defineMessages({
  pageTitle: {
    id: "intakeFormConfirmInfo.pageTitle",
    defaultMessage: "Genetic Information Session",
  },
  stepIndicator: {
    id: "intakeFormConfirmInfo.stepIndicator",
    defaultMessage: "Step {current} of {total}",
  },
  mainTitle: {
    id: "intakeFormConfirmInfo.mainTitle",
    defaultMessage:
      "We'll help you schedule the appointment. First, please confirm or update your contact information.",
  },
  noOrderTitle: {
    id: "intakeFormConfirmInfo.noOrderTitle",
    defaultMessage:
      "We couldn't find any test order under your name yet. You can still schedule a session. Please confirm or update your contact information to continue.",
  },
  noOrderAccordionQuestion: {
    id: "intakeFormConfirmInfo.noOrderAccordionQuestion",
    defaultMessage: "Why isn't my test order available?",
  },
  noOrderAccordionAnswer: {
    id: "intakeFormConfirmInfo.noOrderAccordionAnswer",
    defaultMessage:
      "Your test order isn't available yet. It can take a few days for your provider's order to be processed and show in your <link>Natera's Patient Portal</link>.",
  },
  mobileNumber: {
    id: "intakeFormConfirmInfo.mobileNumber",
    defaultMessage: "Mobile number",
  },
  email: {
    id: "intakeFormConfirmInfo.email",
    defaultMessage: "Email",
  },
  confirmButton: {
    id: "intakeFormConfirmInfo.confirmButton",
    defaultMessage: "Confirm",
  },
  editButton: {
    id: "intakeFormConfirmInfo.editButton",
    defaultMessage: "Edit",
  },
  saveButton: {
    id: "intakeFormConfirmInfo.saveButton",
    defaultMessage: "Save",
  },
  cancelButton: {
    id: "intakeFormConfirmInfo.cancelButton",
    defaultMessage: "Cancel",
  },
  backButton: {
    id: "intakeFormConfirmInfo.backButton",
    defaultMessage: "Back",
  },
  backButtonAria: {
    id: "intakeFormConfirmInfo.backButtonAria",
    defaultMessage: "Back to previous step",
  },
  fieldRequired: {
    id: "intakeFormConfirmInfo.fieldRequired",
    defaultMessage: "Please fill in this field.",
  },
  phoneInvalid: {
    id: "intakeFormConfirmInfo.phoneInvalid",
    defaultMessage: "Please enter a valid phone number.",
  },
  emailInvalid: {
    id: "intakeFormConfirmInfo.emailInvalid",
    defaultMessage: "Please enter a valid email address.",
  },
});

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  inputType?: "text" | "email" | "tel";
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const InputField: FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  onPaste,
  inputType = "text",
  placeholder,
  error,
  disabled = false,
}) => (
  <div className="intake-form__field">
    <label className="intake-form__field__label">{label}</label>
    <input
      type={inputType}
      className={`intake-form__field__input ${error ? "intake-form__field__input--error" : ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPaste={onPaste}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={!!error}
      aria-describedby={error ? `${label}-error` : undefined}
    />
    {error && (
      <span
        id={`${label}-error`}
        className="intake-form__field__error"
        role="alert"
      >
        {error}
      </span>
    )}
  </div>
);

const IntakeFormConfirmInfo: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { config } = useContext(ConfigContext);
  const {
    testCards,
    getTestHistory,
    isLoading: testHistoryIsLoading,
  } = useContext(TestHistoryContext);
  // Order-independent contact info for the no-order flow. `getPatientBasicInfo`
  // is the same backend source (`getProfileBasicInfo`) the order-scoped GIS
  // intake query uses for phone / email, so it's the canonical prefill source;
  // `profileData` (auto-loaded by ProfileProvider) is a fallback.
  const {
    getPatientBasicInfo,
    getPatientBasicInfoData,
    getPatientBasicInfoIsLoading,
    profileData,
    profileDataIsLoading,
  } = useContext(ProfileContext);
  // Final fallback: the authenticated UPP user's contact info, which comes from
  // auth/Okta rather than the patient service, so it stays available even when
  // the profile lookups fail (e.g. patient-service 401).
  const { uppUser } = useContext(UserContext);
  const {
    confirmedPhone: contextPhone,
    confirmedEmail: contextEmail,
    setConfirmedContact,
    setPrefilledData,
    setCounselingIntent,
    totalSteps,
    getStepNumber,
    getNextStep,
  } = useIntakeForm();

  // Once the appointment is booked the intake answers are locked; bounce the
  // patient out of this editable step so they can't navigate back and edit.
  const bookingConfirmedRedirect = useBookingConfirmedRedirect();

  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  // Reached when the GIS triage page finds no eligible order for the patient.
  // We still let them schedule a session, so this step renders the "no test
  // order" messaging instead of the standard confirm-info copy and skips all
  // order-scoped data fetching / persistence.
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  const { data } = useQuery<{ getTestCardDetails: TestDetailsRecord }>(
    TestCardService.getTestCardDetails(),
    {
      variables: { orderUidPublic, testUid },
      fetchPolicy: "cache-first",
      skip: isNoOrder,
    }
  );
  const latestTest = data?.getTestCardDetails ?? null;

  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);

  // Determine which test to use
  const testToUse =
    latestTest || (testCards && testCards.length > 0 ? testCards[0] : null);

  // Used by `handleBack` to detect the public-entry single-order case. A
  // patient with exactly one GIS-eligible order is auto-routed from triage
  // straight into this intake flow, so "Back" should leave the flow rather
  // than point at a test-result page they never visited.
  const { eligibleOrders } = useEligibleGisOrders();

  // Ref to track if we've already fetched test history
  const hasFetchedTestHistory = useRef(false);

  // Fetch test history if no latestTest from state (only once). Skipped in the
  // no-order flow where there is no order/test to back the history lookup.
  useEffect(() => {
    if (
      !isNoOrder &&
      !latestTest &&
      !testCards?.length &&
      !hasFetchedTestHistory.current
    ) {
      hasFetchedTestHistory.current = true;
      getTestHistory();
    }
  }, [isNoOrder, latestTest, testCards?.length, getTestHistory]);

  // Fetch the patient's profile basic info (once) to back the phone / email
  // prefill. In the no-order flow it's the only contact source. In the
  // order-scoped flow it's a fallback for when the order-scoped GIS intake
  // query is unavailable or returns no contact info — e.g. a patient with
  // multiple tests who selects an order whose server-side ownership /
  // test-detail lookup fails, which would otherwise leave both fields blank.
  const hasFetchedBasicInfo = useRef(false);
  useEffect(() => {
    if (!hasFetchedBasicInfo.current) {
      hasFetchedBasicInfo.current = true;
      void getPatientBasicInfo();
    }
  }, [getPatientBasicInfo]);

  // Use the new backend service hook to fetch all intake form data in one call
  // This replaces multiple separate queries (patient info, profile, test details, GIS appointment check)
  const {
    intakeFormData,
    isLoading: intakeFormLoading,
    error: intakeFormError,
  } = useGisIntakeFormData(
    isNoOrder ? null : (orderUidPublic ?? null),
    isNoOrder ? null : (testUid ?? null)
  );

  // Log errors
  useEffect(() => {
    if (intakeFormError) {
      console.error("Intake form data fetch ERROR:", intakeFormError);
    }
  }, [intakeFormError]);

  useEffect(() => {
    if (intakeFormData) {
      setPrefilledData(intakeFormData);
    }
  }, [intakeFormData, setPrefilledData]);

  // Classify the counseling intent (pre-test vs post-test) from the selected
  // order's test status as soon as it is available. Persisting it in the
  // intake context lets every downstream step (and Acuity submission) read
  // the same label without re-deriving it.
  useEffect(() => {
    const status = testToUse?.status;
    if (!status) return;
    setCounselingIntent(classifyCounselingIntent(status));
  }, [testToUse?.status, setCounselingIntent]);

  // Phone validation hook from platform
  const { validatePhone: validatePhoneFormat } = usePhoneValidation();

  const [fetchActiveSession] = useLazyQuery<GetActiveOncUppGisTriageResponse>(
    IntakeFormService.getActiveOncUppGisTriage()
  );
  const [updateTriageSessionStep] =
    useMutation<UpdateOncUppGisTriageStepResponse>(
      IntakeFormService.updateOncUppGisTriageStep()
    );
  const [markTriageIntakeStarted] =
    useMutation<MarkOncUppGisTriageIntakeStartedResponse>(
      IntakeFormService.markOncUppGisTriageIntakeStarted()
    );

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [emailValue, setEmailValue] = useState<string>("");
  const [savedPhone, setSavedPhone] = useState<string>("");
  const [savedEmail, setSavedEmail] = useState<string>("");

  // Validation errors
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const validatePhoneField = (phone: string): string => {
    const cleaned = stripPhoneFormatting(phone);
    if (!cleaned || cleaned === "+") {
      return intl.formatMessage(messages.fieldRequired);
    }
    if (!validatePhoneFormat(cleaned)) {
      return intl.formatMessage(messages.phoneInvalid);
    }
    return "";
  };

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const validateEmailField = (email: string): string => {
    if (!email || email.trim() === "") {
      return intl.formatMessage(messages.fieldRequired);
    }
    if (!validateEmail(email, usePlusSignInEmailAddress)) {
      return intl.formatMessage(messages.emailInvalid);
    }
    return "";
  };

  const currentStep = getStepNumber(GisIntakeStep.CONFIRM_INFO);
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Combined loading state
  const isLoading =
    testHistoryIsLoading ||
    intakeFormLoading ||
    (isNoOrder && getPatientBasicInfoIsLoading && profileDataIsLoading);

  // Order-independent contact fallback built from the same sources the server
  // itself derives phone / email from: the basic info / profile lookups, then
  // the authenticated user so the fields still prefill when the patient service
  // is unavailable.
  const fallbackContact =
    getPatientBasicInfoData ??
    profileData ??
    (uppUser ? { phone: uppUser.phone, email: uppUser.email } : undefined);

  // Prefill source: the order-scoped GIS intake data normally, or the patient's
  // contact info in the no-order flow where no order exists. In the
  // order-scoped flow, fall back to the order-independent contact info per
  // field so a patient with multiple tests still gets prefilled phone / email
  // when the order-scoped intake query fails or returns no contact info.
  const prefillContact = isNoOrder
    ? fallbackContact
      ? {
          phone: fallbackContact.phone ?? "",
          email: fallbackContact.email ?? "",
        }
      : null
    : intakeFormData || fallbackContact
      ? {
          phone: intakeFormData?.phone || fallbackContact?.phone || "",
          email: intakeFormData?.email || fallbackContact?.email || "",
        }
      : null;

  // Initialize field values: prefer context (user-confirmed) over backend (pre-filled)
  useEffect(() => {
    if (contextPhone && contextEmail) {
      const phone = formatPhoneNumberInput(contextPhone);
      const email = contextEmail;
      setPhoneValue(phone);
      setEmailValue(email);
      setSavedPhone(phone);
      setSavedEmail(email);
    } else if (prefillContact) {
      const phone = formatPhoneNumberInput(prefillContact.phone || "");
      const email = prefillContact.email || "";
      setPhoneValue(phone);
      setEmailValue(email);
      setSavedPhone(phone);
      setSavedEmail(email);
    }
  }, [
    prefillContact?.phone,
    prefillContact?.email,
    contextPhone,
    contextEmail,
  ]);

  // Edit mode handlers
  const handleEdit = () => {
    setPhoneError("");
    setEmailError("");
    setIsEditMode(true);
  };

  const handleSave = () => {
    const phoneValidationError = validatePhoneField(phoneValue);
    const emailValidationError = validateEmailField(emailValue);

    setPhoneError(phoneValidationError);
    setEmailError(emailValidationError);

    if (phoneValidationError || emailValidationError) {
      return;
    }

    setSavedPhone(phoneValue);
    setSavedEmail(emailValue);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setPhoneValue(savedPhone);
    setEmailValue(savedEmail);
    setPhoneError("");
    setEmailError("");
    setIsEditMode(false);
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPhoneValue(formatPastedPhoneNumber(e.clipboardData.getData("text")));
    if (phoneError) {
      setPhoneError("");
    }
  };

  const handlePhoneChange = (value: string) => {
    setPhoneValue(formatPhoneNumberInput(value));
    if (phoneError) {
      setPhoneError("");
    }
  };

  const handleEmailChange = (value: string) => {
    setEmailValue(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleBack = () => {
    // No backing order means there's no test-result page to return to — leave
    // the GIS flow for the portal home and drop the stored return URL so a
    // later re-auth doesn't bounce the user back into triage.
    if (isNoOrder) {
      clearGisReturnUrl();
      navigate(routes.home);
      return;
    }

    // Public-entry patient with a single pre-test order: triage auto-routes
    // them from the public funnel straight into this intake flow, so they
    // never land on a test-result page (and a pre-test order has no results to
    // show anyway). Returning "Back" to that page would strand them somewhere
    // they never visited, so send them to the portal home and drop the stored
    // return URL so a later re-auth doesn't bounce them back into triage.
    const isPublicEntry = getGisEntrySource() === GIS_ENTRY_SOURCE_PUBLIC;
    const isPreTest =
      classifyCounselingIntent(testToUse?.status) === "pre-test";
    const isSingleOrder = eligibleOrders.length === 1;
    if (isPublicEntry && isPreTest && isSingleOrder) {
      clearGisReturnUrl();
      navigate(routes.home);
      return;
    }

    // Public-entry patient with more than one eligible order reached this
    // intake flow by first choosing an order in the order-selection page, so
    // "Back" returns them to that picker (still inside the GIS flow, so the
    // return URL is left intact) rather than a single test-result page.
    if (isPublicEntry && eligibleOrders.length > 1) {
      navigate(routes.gisOrderSelect);
      return;
    }

    navigate(routes.testResultDetailsPage(orderUidPublic || "", testUid || ""));
  };

  const handleConfirm = () => {
    const phoneValidationError = validatePhoneField(phoneValue);
    const emailValidationError = validateEmailField(emailValue);

    setPhoneError(phoneValidationError);
    setEmailError(emailValidationError);

    if (phoneValidationError || emailValidationError) {
      return;
    }

    const strippedPhone = stripPhoneFormatting(phoneValue);
    setConfirmedContact(strippedPhone, emailValue);

    // Fire-and-forget: persist the confirmed contact info to the active
    // triage row so edits survive an abandonment mid-flow, and stamp
    // `intake_started` to mark the beginning of the intake flow. Failure
    // is non-blocking — the final save in scheduleSessionPage will retry
    // the contact-info update. The no-order flow has a triage row too (created
    // up-front in PublicGisTriage with the sentinel uids), so it persists here
    // as well.
    if (orderUidPublic && testUid) {
      void (async () => {
        try {
          const { data } = await fetchActiveSession({
            variables: { orderUidPublic, testUid },
            fetchPolicy: "network-only",
          });
          const sessionId = data?.getActiveOncUppGisTriage?.id;
          if (sessionId == null) {
            return;
          }
          await updateTriageSessionStep({
            variables: {
              sessionId,
              input: {},
              confirmedPhone: strippedPhone,
              confirmedEmail: emailValue,
              prefilledInput: {
                phone: strippedPhone,
                email: emailValue,
              },
            },
          });
          await markTriageIntakeStarted({ variables: { sessionId } });
        } catch (err) {
          console.error("Failed to persist confirmed contact info:", err);
        }
      })();
    }

    const nextStep =
      getNextStep(GisIntakeStep.CONFIRM_INFO) ?? GisIntakeStep.ACTIVE_DISEASE;
    navigate(
      routes.scheduleAGeneticInformationSessionIntakePage(
        orderUidPublic || "",
        testUid || ""
      ) + STEP_ROUTE_SUFFIX[nextStep]
    );
  };

  if (bookingConfirmedRedirect) {
    return bookingConfirmedRedirect;
  }

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.pageTitle)}
      backArrowHeaderButton={handleBack}
      medium
    >
      <div className="intake-form">
        <SpinnerView isLoading={isLoading} />
        <div className="intake-form__content">
          <div className="intake-form__card-container">
            <div className="intake-form__progress-bar">
              <div
                className="intake-form__progress-bar__fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            <div className="intake-form__card">
              <div className="intake-form__title-section">
                <span className="intake-form__step-indicator">
                  {intl.formatMessage(messages.stepIndicator, {
                    current: currentStep,
                    total: totalSteps,
                  })}
                </span>
                <h2 className="intake-form__main-title">
                  {intl.formatMessage(
                    isNoOrder ? messages.noOrderTitle : messages.mainTitle
                  )}
                </h2>
              </div>

              {isNoOrder && (
                <div className="intake-form__accordion">
                  <button
                    type="button"
                    className="intake-form__accordion__header"
                    onClick={() => setIsAccordionOpen((open) => !open)}
                    aria-expanded={isAccordionOpen}
                  >
                    <Svg
                      className="intake-form__accordion__info"
                      content={InfoIcon}
                    />
                    <span className="intake-form__accordion__question">
                      {intl.formatMessage(messages.noOrderAccordionQuestion)}
                    </span>
                    <Svg
                      className="intake-form__accordion__chevron"
                      content={isAccordionOpen ? ChevronUp : ChevronDown}
                    />
                  </button>
                  {isAccordionOpen && (
                    <p className="intake-form__accordion__body">
                      {intl.formatMessage(messages.noOrderAccordionAnswer, {
                        link: (...chunks) => (
                          <a
                            className="intake-form__accordion__link"
                            href={routes.home}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {chunks}
                          </a>
                        ),
                      })}
                    </p>
                  )}
                </div>
              )}

              <div className="intake-form__fields">
                <InputField
                  label={intl.formatMessage(messages.mobileNumber)}
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  onPaste={handlePhonePaste}
                  inputType="tel"
                  placeholder="+1-555-123-4567"
                  error={phoneError}
                  disabled={!isEditMode}
                />
                <InputField
                  label={intl.formatMessage(messages.email)}
                  value={emailValue}
                  onChange={handleEmailChange}
                  inputType="email"
                  placeholder="jsmith@gmail.com"
                  error={emailError}
                  disabled={!isEditMode}
                />
              </div>

              <div className="intake-form__actions">
                {isEditMode ? (
                  <>
                    <Button
                      className="intake-form__actions__confirm"
                      unelevated
                      onClick={handleSave}
                    >
                      {intl.formatMessage(messages.saveButton)}
                    </Button>
                    <Button
                      className="intake-form__actions__edit"
                      outlined
                      onClick={handleCancelEdit}
                    >
                      {intl.formatMessage(messages.cancelButton)}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      className="intake-form__actions__confirm"
                      unelevated
                      onClick={handleConfirm}
                    >
                      {intl.formatMessage(messages.confirmButton)}
                    </Button>
                    <Button
                      className="intake-form__actions__edit"
                      outlined
                      onClick={handleEdit}
                    >
                      {intl.formatMessage(messages.editButton)}
                    </Button>
                    <button
                      className="intake-form__actions__back"
                      onClick={handleBack}
                      aria-label={intl.formatMessage(messages.backButtonAria)}
                    >
                      {intl.formatMessage(messages.backButton)}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlueHeaderContent>
  );
};

export default IntakeFormConfirmInfo;
